import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Box, Button, Slide } from '@mui/material';
import { hideToast } from '../../store/toaster/action';
import {
  runToastAction,
  unregisterToastAction,
} from '../../store/toaster/toastActionRegistry';
import { ToastItem } from '../../helpers/types';

function Toast({ toast }: { toast: ToastItem }) {
  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setTimeout(() => {
      unregisterToastAction(toast.id);
      dispatch(hideToast(toast.id));
    }, toast.duration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id]);

  const handleClose = () => {
    unregisterToastAction(toast.id);
    dispatch(hideToast(toast.id));
  };

  const handleAction = () => {
    runToastAction(toast.id);
    dispatch(hideToast(toast.id));
  };

  return (
    <Slide direction="left" in appear>
      <Alert
        onClose={handleClose}
        severity={(toast.severity as any) || 'info'}
        data-testid="toast-alert"
        action={
          toast.actionLabel ? (
            <>
              <Button
                color="inherit"
                size="small"
                onClick={handleAction}
                data-testid="toast-action-button"
                sx={{ fontWeight: 600 }}
              >
                {toast.actionLabel}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleClose}
                sx={{ minWidth: 'auto', px: 0.5 }}
              >
                ✕
              </Button>
            </>
          ) : undefined
        }
        sx={{ width: '100%', boxShadow: 3, pointerEvents: 'auto' }}
      >
        {toast.message}
        {toast.link && (
          <>
            {' — '}
            <a
              href={toast.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              style={{
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {toast.linkText || 'View'}
            </a>
          </>
        )}
      </Alert>
    </Slide>
  );
}

function ToastMui() {
  const toasts = useSelector(
    (state: any) => state.toaster.toasts,
  ) as ToastItem[];

  if (!toasts?.length) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: theme => theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: 'auto',
        maxWidth: 420,
        pointerEvents: 'none',
      }}
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} />
      ))}
    </Box>
  );
}

export default ToastMui;
